/**
 * A sort choice that is in the address *and* remembered.
 *
 * Two requirements that pull in opposite directions, and both are real:
 *
 * 1. **The URL is the state**, as it is for search and paging — so a sorted list can be refreshed,
 *    gone back to, and pasted to a colleague and still be the same list.
 * 2. **The choice sticks.** Somebody who sorts their properties by name means it; arriving from the
 *    rail tomorrow and finding them newest-first again is the application forgetting something the
 *    user told it.
 *
 * So: the address wins when it says anything, and what it says is written to `localStorage`. A
 * visit with no `?sort` reads the remembered one and puts it *into* the address, which keeps the
 * two from ever disagreeing — every later read has one source.
 *
 * `localStorage` rather than the server: it is a per-person, per-device preference about how a
 * screen looks, not a fact about the agency, and round-tripping it through the API would make
 * every list wait on a second request to know how to draw itself. It is wrapped because a browser
 * in private mode throws on access rather than returning null.
 */

import { useEffect } from "react";
import { useSearchParams } from "react-router-dom";

const PREFIX = "rentorng.sort.";

function remembered(key: string): string | null {
  try {
    return window.localStorage.getItem(PREFIX + key);
  } catch {
    return null;
  }
}

function remember(key: string, value: string): void {
  try {
    window.localStorage.setItem(PREFIX + key, value);
  } catch {
    /* Private mode, or storage switched off. The sort still works for this visit; it just will
       not be there tomorrow, which is a far better failure than a screen that will not draw. */
  }
}

export interface SortOption {
  value: string;
  label: string;
}

export interface StickySort {
  /** What to send to the server, and what the control shows as chosen. */
  sort: string;
  setSort: (next: string) => void;
  options: SortOption[];
}

/**
 * @param key      Which list this is. One remembered choice per list, not one for the whole app.
 * @param options  What may be chosen. The first is the default.
 */
export function useStickySort(key: string, options: SortOption[]): StickySort {
  const [params, setParams] = useSearchParams();
  const valid = new Set(options.map((option) => option.value));

  const fromUrl = params.get("sort");
  const stored = remembered(key);
  const fallback = options[0].value;

  const sort = fromUrl && valid.has(fromUrl) ? fromUrl : stored && valid.has(stored) ? stored : fallback;

  /*
    Put the resolved choice into the address, once, when it is not already there.

    Without this the remembered sort would be applied but invisible — the list would come back
    ordered by name with `?sort` absent, and a link copied from it would open unsorted for
    somebody else. `replace` so it does not add a history entry nobody asked for.
  */
  useEffect(() => {
    if (fromUrl === sort) return;
    setParams(
      (current) => {
        const next = new URLSearchParams(current);
        next.set("sort", sort);
        return next;
      },
      { replace: true },
    );
  }, [fromUrl, sort, setParams]);

  useEffect(() => {
    remember(key, sort);
  }, [key, sort]);

  return {
    sort,
    options,
    setSort: (next) => {
      remember(key, next);
      setParams((current) => {
        const params = new URLSearchParams(current);
        params.set("sort", next);
        // Re-sorting shows different rows first, so page 4 is meaningless. Same rule as searching.
        params.delete("page");
        return params;
      });
    },
  };
}
